/**
 * WP4e / #46611 — verification-operate contracts for Workers/queue health,
 * Scorecard, Runtime/keys/posture, and API catalog/auth-scope metadata.
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`.
 *
 * HG11 (#46595): mutations use existing ROLE_CAPABILITIES — no new
 * capabilities. Legacy token mutation contracts remain typed for compatibility
 * but the product route fails them closed until they use the durable bearer store.
 * Reads are ungated at the Hono seam, same as WP4a overview.
 *
 * WP4a–WP4d contracts are out of scope — do not edit those files.
 * Demo seed/reset is WP3. Schema is WP4g. Operate UI pages are WP6m–WP6o.
 */

import { z } from "zod";
import type { Operation } from "../types";
import { verificationDefine } from "./verification-define";
import { verificationEvidence } from "./verification-evidence";
import { verificationProve } from "./verification-prove";
import { verificationShell } from "./verification-shell";
import {
  durableLoopJobSchema,
  operationalDurabilityReportSchema,
} from "@excellent/core/verification-protocol-client";

export const VERIFICATION_OPERATE_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
export const verificationOperateEnvQuery = z.object({
  env: envName.optional(),
  actorId: z.string().min(1).max(200).optional(),
});
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);
const workerRole = z.enum([
  "semantic-check",
  "connector-sync",
  "receipt-replay",
  "backfill",
  "audit-export",
]);
const workerStatus = z.enum(["healthy", "starting", "stopped", "failed"]);
const metricStatus = z.enum(["pass", "warn", "fail", "blocked"]);
const metricDirection = z.enum(["higher-is-better", "lower-is-better"]);
const runtimeCheckStatus = z.enum(["pass", "warn", "fail"]);
const runtimeMode = z.enum(["unconfigured", "cloud", "private-vpc", "local-offline"]);
const postureStatus = z.enum(["answered", "answered-with-caveat", "not-yet"]);
const postureTopic = z.enum([
  "custody",
  "scopes",
  "egress",
  "anchoring",
  "tenancy",
  "vendor-assurance",
  "ai-boundary",
]);
const httpMethod = z.enum(["GET", "POST", "PATCH", "DELETE"]);
const endpointStability = z.enum(["stable", "beta"]);
const componentName = z.enum([
  "api",
  "ui",
  "worker",
  "database",
  "artifact-store",
  "signer",
  "queue",
  "integration",
  "projection",
]);
const componentHealthStatus = z.enum(["healthy", "degraded", "unavailable", "unknown"]);
const aggregateHealthStatus = z.enum(["operational", "degraded", "blocked"]);

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export type VerificationOperateEnvQuery = z.infer<typeof verificationOperateEnvQuery>;
export type VerificationOperateProvenance = z.infer<typeof provenance>;

export const verificationWorkerDto = z.object({
  id: namespacedId,
  role: workerRole,
  model: z.object({
    provider: z.string().min(1),
    name: z.string().min(1),
    version: z.string().min(1),
  }),
  calibrated: z.boolean(),
  status: workerStatus,
  lastHeartbeatAt: iso,
  inBoundary: z.boolean(),
  activeJobId: z.string().nullable(),
  queued: z.number().int().nonnegative(),
  succeeded24h: z.number().int().nonnegative(),
  failed24h: z.number().int().nonnegative(),
});
export type VerificationWorkerDto = z.infer<typeof verificationWorkerDto>;

export const verificationQueueHealthDto = z.object({
  queued: z.number().int().nonnegative(),
  failed24h: z.number().int().nonnegative(),
  unhealthyCount: z.number().int().nonnegative(),
  lastHeartbeatAt: iso.nullable(),
});
export type VerificationQueueHealthDto = z.infer<typeof verificationQueueHealthDto>;

export const verificationComponentHealthObservationDto = z.object({
  component: componentName,
  status: componentHealthStatus,
  observedAt: iso,
  maxAgeSeconds: z.number().positive(),
  capabilities: z.array(z.string().min(1)).min(1),
  detailCode: z.string().min(1),
});
export type VerificationComponentHealthObservationDto = z.infer<
  typeof verificationComponentHealthObservationDto
>;

export const verificationComponentHealthAssessmentDto = z.object({
  component: componentName,
  status: componentHealthStatus,
  ready: z.boolean(),
  stale: z.boolean(),
  affectedCapabilities: z.array(z.string().min(1)),
  reason: z.string().min(1),
});
export type VerificationComponentHealthAssessmentDto = z.infer<
  typeof verificationComponentHealthAssessmentDto
>;

export const verificationMetricDto = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  value: z.string().min(1),
  numeric: z.number(),
  target: z.string().min(1),
  status: metricStatus,
  direction: metricDirection,
  note: z.string().min(1),
  lastEvalAt: iso.nullable(),
});
export type VerificationMetricDto = z.infer<typeof verificationMetricDto>;

export const verificationRuntimeCheckDto = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  status: runtimeCheckStatus,
  detail: z.string().min(1),
});
export type VerificationRuntimeCheckDto = z.infer<typeof verificationRuntimeCheckDto>;

export const verificationSigningKeyDto = z.object({
  id: z.string().min(1),
  custody: z.string().min(1),
  lastRotatedAt: iso.nullable(),
  rotationPolicyDays: z.number().int().positive(),
  nextRotationAt: iso.nullable(),
});
export type VerificationSigningKeyDto = z.infer<typeof verificationSigningKeyDto>;

export const verificationPostureQuestionDto = z.object({
  id: z.string().min(1),
  topic: postureTopic,
  question: z.string().min(1),
  answer: z.string().min(1),
  status: postureStatus,
  caveat: z.string().min(1).nullable(),
  evidenceLink: z.string().min(1).nullable(),
  runtimeModes: z.array(runtimeMode),
});
export type VerificationPostureQuestionDto = z.infer<typeof verificationPostureQuestionDto>;

/**
 * One row of the published API catalogue.
 *
 * Every field here is a fact the catalogue owns, so every field here is
 * transcribed from it rather than authored — see `VERIFICATION_PUBLIC_CATALOG`
 * in `excellent/server/verification-operate.ts`, which derives the whole list
 * from `describeVerificationApiSurface()`. `check-verification-api-page-derived`
 * reads this shape to decide what the page must render, so a field added here
 * is a field the page has to show.
 *
 * `served` and `implemented` are deliberately two booleans, not one. Withheld
 * means the operation is not on the public surface at all and a token can never
 * reach it. Declared-not-implemented means it is reachable, scoped and
 * documented, and the engine refuses it with 501 and a reason that names the
 * path which does work. Collapsing them would publish an actionable refusal as
 * a 404, and a product that advertises a capability indistinguishably from the
 * ones it performs is making the class of claim this product exists to refuse.
 */
export const verificationApiEndpointDto = z.object({
  id: z.string().min(1),
  method: httpMethod,
  path: z.string().min(1),
  resource: z.string().min(1),
  scope: z.string().min(1),
  stability: endpointStability,
  summary: z.string().min(1),
  served: z.boolean(),
  withheldReason: z.string().min(1).nullable(),
  implemented: z.boolean(),
  notImplementedReason: z.string().min(1).nullable(),
});
export type VerificationApiEndpointDto = z.infer<typeof verificationApiEndpointDto>;

export const verificationApiTokenDto = z.object({
  id: namespacedId,
  label: z.string().min(1),
  scopes: z.array(z.string().min(1)).min(1),
  createdAt: iso,
  expiresAt: iso,
  lastUsedAt: iso.nullable(),
  revokedAt: iso.nullable(),
  /** Visible prefix only. The raw secret is never listed. */
  prefix: z.string().min(1),
});
export type VerificationApiTokenDto = z.infer<typeof verificationApiTokenDto>;

const idParams = z.object({ id: namespacedId });
export type VerificationOperateIdParams = z.infer<typeof idParams>;

export const verificationWorkersListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  queueHealth: verificationQueueHealthDto,
  items: z.array(verificationWorkerDto),
});
export type VerificationWorkersListResponse = z.infer<typeof verificationWorkersListResponse>;

export const verificationScorecardGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  metrics: z.array(verificationMetricDto),
});
export type VerificationScorecardGetResponse = z.infer<typeof verificationScorecardGetResponse>;

export const verificationRuntimeGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  runtimeMode,
  checks: z.array(verificationRuntimeCheckDto),
  signingKey: verificationSigningKeyDto.nullable(),
  posture: z.array(verificationPostureQuestionDto),
});
export type VerificationRuntimeGetResponse = z.infer<typeof verificationRuntimeGetResponse>;

export const verificationCatalogGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  surface: z.object({
    mountPath: z.literal("/api/verification/v1"),
    mounted: z.boolean(),
    mountReason: z.literal("not-enabled").nullable(),
    handlerProvenance: z.enum(["engine", "demo"]).nullable(),
    credentialSource: z.literal("durable-store"),
    credentialManagement: z.literal("not-available-in-product-ui"),
  }),
  endpoints: z.array(verificationApiEndpointDto),
  tokens: z.array(verificationApiTokenDto),
});
export type VerificationCatalogGetResponse = z.infer<typeof verificationCatalogGetResponse>;

export const verificationHealthGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  tenantId: z.string().min(1),
  deploymentId: z.string().min(1),
  generatedAt: iso,
  healthStatus: aggregateHealthStatus,
  components: z.array(verificationComponentHealthAssessmentDto),
  affectedCapabilities: z.array(z.string().min(1)),
});
export type VerificationHealthGetResponse = z.infer<typeof verificationHealthGetResponse>;

export const verificationReadinessGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  tenantId: z.string().min(1),
  deploymentId: z.string().min(1),
  generatedAt: iso,
  ready: z.boolean(),
  healthStatus: aggregateHealthStatus,
  affectedCapabilities: z.array(z.string().min(1)),
  blockedReasons: z.array(z.string().min(1)),
});
export type VerificationReadinessGetResponse = z.infer<typeof verificationReadinessGetResponse>;

export const verificationLocalEnvironmentBody = z.object({
  actorId: namespacedId,
});
export type VerificationLocalEnvironmentBody = z.infer<typeof verificationLocalEnvironmentBody>;

export const verificationLocalEnvironmentResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  environment: envName,
  tenantId: namespacedId,
  organizationId: namespacedId,
  seedVersion: z.string().min(1),
  recordCount: z.number().int().nonnegative(),
});
export type VerificationLocalEnvironmentResponse = z.infer<
  typeof verificationLocalEnvironmentResponse
>;

export const verificationOperationalSchedulerTickBody = z.object({
  actorId: namespacedId,
  workflowId: z.string().min(1).max(200),
  environmentId: z.string().min(1).max(200),
  baseReceiptDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  maxJobs: z.number().int().positive().max(50),
});
export type VerificationOperationalSchedulerTickBody = z.infer<
  typeof verificationOperationalSchedulerTickBody
>;

export const verificationOperationalSchedulerTickResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  tenantId: namespacedId,
  completedJobs: z.number().int().nonnegative(),
  operationalEvidenceOrigin: z.enum(["synthetic_fixture", "not_observed"]),
  durableLoopJobs: z.array(durableLoopJobSchema),
  operationalDurability: operationalDurabilityReportSchema,
  durableRunnerJobCount: z.number().int().nonnegative(),
  durableRunnerEffectCount: z.number().int().nonnegative(),
});
export type VerificationOperationalSchedulerTickResponse = z.infer<
  typeof verificationOperationalSchedulerTickResponse
>;

export const verificationTokenCreateBody = z.object({
  actorId: namespacedId,
  label: z.string().min(1).max(120),
  scopes: z.array(z.string().min(1)).min(1),
  expiresAt: iso.nullable().optional(),
});
export type VerificationTokenCreateBody = z.infer<typeof verificationTokenCreateBody>;

export const verificationTokenCreateResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  token: verificationApiTokenDto,
  /** One-time secret. Never persisted on later reads. */
  secret: z.string().min(16),
});
export type VerificationTokenCreateResponse = z.infer<typeof verificationTokenCreateResponse>;

export const verificationTokenRevokeBody = z.object({
  actorId: namespacedId,
});
export type VerificationTokenRevokeBody = z.infer<typeof verificationTokenRevokeBody>;

export const verificationTokenRevokeResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  token: verificationApiTokenDto,
});
export type VerificationTokenRevokeResponse = z.infer<typeof verificationTokenRevokeResponse>;

export const verificationTokenRotateBody = z.object({
  actorId: namespacedId,
  expiresAt: iso.nullable().optional(),
});
export type VerificationTokenRotateBody = z.infer<typeof verificationTokenRotateBody>;

export const verificationTokenRotateResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_OPERATE_SCHEMA_VERSION),
  provenance,
  capability: z.string().min(1),
  revokedToken: verificationApiTokenDto,
  token: verificationApiTokenDto,
  /** One-time replacement secret. Never persisted on later reads. */
  secret: z.string().min(16),
});
export type VerificationTokenRotateResponse = z.infer<typeof verificationTokenRotateResponse>;

const workersListOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationWorkersListResponse
> = {
  method: "GET",
  path: () => "/api/verification/workers",
  query: verificationOperateEnvQuery,
  response: verificationWorkersListResponse,
};

const scorecardGetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationScorecardGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/scorecard",
  query: verificationOperateEnvQuery,
  response: verificationScorecardGetResponse,
};

const runtimeGetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationRuntimeGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/runtime",
  query: verificationOperateEnvQuery,
  response: verificationRuntimeGetResponse,
};

const catalogGetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationCatalogGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/api-catalog",
  query: verificationOperateEnvQuery,
  response: verificationCatalogGetResponse,
};

const healthGetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationHealthGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/health",
  query: verificationOperateEnvQuery,
  response: verificationHealthGetResponse,
};

const readinessGetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  void,
  VerificationReadinessGetResponse
> = {
  method: "GET",
  path: () => "/api/verification/readiness",
  query: verificationOperateEnvQuery,
  response: verificationReadinessGetResponse,
};

const tokensCreateOp: Operation<
  void,
  VerificationOperateEnvQuery,
  VerificationTokenCreateBody,
  VerificationTokenCreateResponse
> = {
  method: "POST",
  path: () => "/api/verification/api-tokens",
  query: verificationOperateEnvQuery,
  body: verificationTokenCreateBody,
  response: verificationTokenCreateResponse,
  invalidates: [catalogGetOp],
};

const localEnvironmentInvalidates = [
  verificationShell.overview,
  verificationShell.queue,
  verificationShell.findingsList,
  verificationShell.runsList,
  verificationDefine.workflowsList,
  verificationDefine.controlsList,
  verificationDefine.policiesList,
  verificationEvidence.artifactsList,
  verificationEvidence.connectorsList,
  verificationProve.receiptsList,
  verificationProve.packsList,
  verificationProve.auditRoomGet,
  workersListOp,
  scorecardGetOp,
  runtimeGetOp,
  catalogGetOp,
] as const;

const localEnvironmentsSpawnOp: Operation<
  void,
  VerificationOperateEnvQuery,
  VerificationLocalEnvironmentBody,
  VerificationLocalEnvironmentResponse
> = {
  method: "POST",
  path: () => "/api/verification/local-environments/spawn",
  query: verificationOperateEnvQuery,
  body: verificationLocalEnvironmentBody,
  response: verificationLocalEnvironmentResponse,
  invalidates: [...localEnvironmentInvalidates],
};

const localEnvironmentsResetOp: Operation<
  void,
  VerificationOperateEnvQuery,
  VerificationLocalEnvironmentBody,
  VerificationLocalEnvironmentResponse
> = {
  method: "POST",
  path: () => "/api/verification/local-environments/reset",
  query: verificationOperateEnvQuery,
  body: verificationLocalEnvironmentBody,
  response: verificationLocalEnvironmentResponse,
  invalidates: [...localEnvironmentInvalidates],
};

const operationalSchedulerTickOp: Operation<
  void,
  VerificationOperateEnvQuery,
  VerificationOperationalSchedulerTickBody,
  VerificationOperationalSchedulerTickResponse
> = {
  method: "POST",
  path: () => "/api/verification/operational-durability/scheduler-tick",
  query: verificationOperateEnvQuery,
  body: verificationOperationalSchedulerTickBody,
  response: verificationOperationalSchedulerTickResponse,
  invalidates: [verificationShell.overview],
};

const tokensRevokeOp: Operation<
  VerificationOperateIdParams,
  VerificationOperateEnvQuery,
  VerificationTokenRevokeBody,
  VerificationTokenRevokeResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/api-tokens/${encodeURIComponent(id)}/revoke`,
  params: idParams,
  query: verificationOperateEnvQuery,
  body: verificationTokenRevokeBody,
  response: verificationTokenRevokeResponse,
  invalidates: [catalogGetOp],
};

const tokensRotateOp: Operation<
  VerificationOperateIdParams,
  VerificationOperateEnvQuery,
  VerificationTokenRotateBody,
  VerificationTokenRotateResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/api-tokens/${encodeURIComponent(id)}/rotate`,
  params: idParams,
  query: verificationOperateEnvQuery,
  body: verificationTokenRotateBody,
  response: verificationTokenRotateResponse,
  invalidates: [catalogGetOp],
};

export const verificationOperate = {
  workersList: workersListOp,
  scorecardGet: scorecardGetOp,
  runtimeGet: runtimeGetOp,
  catalogGet: catalogGetOp,
  healthGet: healthGetOp,
  readinessGet: readinessGetOp,
  localEnvironmentsSpawn: localEnvironmentsSpawnOp,
  localEnvironmentsReset: localEnvironmentsResetOp,
  operationalSchedulerTick: operationalSchedulerTickOp,
  tokensCreate: tokensCreateOp,
  tokensRevoke: tokensRevokeOp,
  tokensRotate: tokensRotateOp,
};
